import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCourts } from '../../../../../services/courtServices';
import {
  getRegiones,
  getTribunalesArea,
} from '../../../../../services/roleoAutomaticoService';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configAreasI18n } from './i18n';

const STATUS_VALUES = {
  active: 'active',
  inactive: 'inactive',
};

const TAB_VALUES = {
  all: 'all',
  assigned: 'assigned',
  unassigned: 'unassigned',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return STATUS_VALUES.inactive;
  }

  return STATUS_VALUES.active;
};

export default function AreaCreateEditModal({
  Modal,
  Input,
  Dropdown,
  Table,
  ComboBox,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const areasLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configAreasI18n });

  const isEdit = mode === 'edit';
  const [local, setLocal] = useState({
    id: null,
    name: '',
    status: STATUS_VALUES.active,
  });
  const [tribunals, setTribunals] = useState([]);
  const [tribunalsLoading, setTribunalsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(TAB_VALUES.all);
  const [regionFilter, setRegionFilter] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    configAreasI18n.changeLanguage(areasLanguage);
  }, [areasLanguage]);

  useEffect(() => {
    if (show) {
      setLocal({
        id: value?.id ?? null,
        name: value?.name ?? '',
        status: normalizeStatus(value?.status),
      });
      setSearch('');
      setTab(TAB_VALUES.all);
    }
  }, [show, value?.id, value?.name, value?.status]);

  useEffect(() => {
    if (!show) return;
    let mounted = true;

    const fetchTribunals = async () => {
      try {
        setTribunalsLoading(true);
        const response = await getAllCourts();
        if (!mounted) return;

        const mapped = (response || [])
          .map((item) => {
            const regionId =
              item.region ?? item.regionId ?? item.regionValue ?? null;
            const regionAlias =
              item.regionAlias ??
              item.regionName ??
              item.nombreRegion ??
              item.regionNombre ??
              null;

            return {
              id: item.value ?? item.id ?? item.codigo ?? item.codigoTribunal,
              name: item.label ?? item.name ?? item.nombre ?? '',
              regionId,
              region: regionAlias ?? regionId,
              type: item.type ?? item.tipo ?? item.tipoTribunal ?? null,
            };
          })
          .filter((item) => item.id != null && item.name);

        setTribunals(mapped);
      } catch (error) {
        console.error('Error loading courts:', error);
      } finally {
        if (mounted) {
          setTribunalsLoading(false);
        }
      }
    };

    fetchTribunals();

    return () => {
      mounted = false;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    let mounted = true;

    const fetchRegions = async () => {
      try {
        setRegionsLoading(true);
        const response = await getRegiones();
        if (!mounted) return;
        setRegions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading regions:', error);
      } finally {
        if (mounted) {
          setRegionsLoading(false);
        }
      }
    };

    fetchRegions();

    return () => {
      mounted = false;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    if (!isEdit || !value?.id) {
      setSelectedIds([]);
      return;
    }
    let mounted = true;

    const fetchAreaTribunals = async () => {
      try {
        const response = await getTribunalesArea(value.id);
        if (!mounted) return;
        const courts = Array.isArray(response?.courts)
          ? response.courts
          : response;
        const ids = Array.isArray(courts)
          ? courts.map((item) =>
              typeof item === 'number' ? item : (item.id ?? item.value ?? null)
            )
          : [];
        setSelectedIds(ids.filter((id) => id != null));
      } catch (error) {
        console.error('Error loading assigned courts:', error);
        setSelectedIds([]);
      }
    };

    fetchAreaTribunals();

    return () => {
      mounted = false;
    };
  }, [show, isEdit, value?.id]);

  const nameValid = useMemo(() => local.name.trim().length > 0, [local.name]);
  const isValid =
    nameValid &&
    [STATUS_VALUES.active, STATUS_VALUES.inactive].includes(
      normalizeStatus(local.status)
    );

  const setField = (key) => (eventOrValue) => {
    const nextValue = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleSave = () => {
    if (!isValid) return;
    const payload = {
      ...local,
      status: normalizeStatus(local.status),
    };
    onChange?.(payload);
    onSave?.(payload, selectedIds);
  };

  const statusOptions = [
    { label: t('config_areas_status_active'), value: STATUS_VALUES.active },
    {
      label: t('config_areas_status_inactive'),
      value: STATUS_VALUES.inactive,
    },
  ];

  const statusOptionFrom = (val) =>
    statusOptions.find((option) => option.value === normalizeStatus(val)) ||
    statusOptions[0];

  const regionOptions = useMemo(() => {
    const values = regions
      .map((region) => region.alias || region.name || region.label || '')
      .filter(Boolean)
      .sort();

    return [
      {
        label: t('config_areas_modal_regions_all'),
        value: TAB_VALUES.all,
      },
      ...values.map((region) => ({ label: region, value: region })),
    ];
  }, [regions, t]);

  useEffect(() => {
    if (!show) return;
    if (regionOptions.length === 0) return;
    const currentValue = regionFilter?.value ?? TAB_VALUES.all;
    if (!regionOptions.some((opt) => opt.value === currentValue)) {
      setRegionFilter(regionOptions[0] ?? null);
    }
  }, [show, regionOptions, regionFilter]);

  const filterOptionFrom = (options, val) =>
    options.find((opt) => opt.value === val) ?? options[0] ?? null;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTribunals = useMemo(() => {
    return tribunals.filter((tribunal) => {
      if (regionFilter?.value && regionFilter.value !== TAB_VALUES.all) {
        if (tribunal.region !== regionFilter.value) return false;
      }

      const isAssigned = selectedIds.includes(tribunal.id);
      if (tab === TAB_VALUES.assigned && !isAssigned) return false;
      if (tab === TAB_VALUES.unassigned && isAssigned) return false;

      if (!normalizedSearch) return true;
      const haystack = `${tribunal.name || ''} ${tribunal.region || ''} ${
        tribunal.type || ''
      }`
        .toLowerCase()
        .trim();

      return haystack.includes(normalizedSearch);
    });
  }, [tribunals, normalizedSearch, regionFilter, tab, selectedIds]);

  const filteredIds = filteredTribunals.map((tribunal) => tribunal.id);
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.includes(id));
  const someFilteredSelected =
    filteredIds.some((id) => selectedIds.includes(id)) && !allFilteredSelected;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = someFilteredSelected;
  }, [someFilteredSelected]);

  const toggleTribunal = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      if (filteredIds.length === 0) return prev;

      if (allFilteredSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }

      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const tabOptions = [
    { label: t('config_areas_modal_tab_all'), value: TAB_VALUES.all },
    {
      label: t('config_areas_modal_tab_assigned', { count: selectedIds.length }),
      value: TAB_VALUES.assigned,
    },
    {
      label: t('config_areas_modal_tab_unassigned'),
      value: TAB_VALUES.unassigned,
    },
  ];

  if (!Modal || !Input) return null;

  return (
    <Modal
      title={
        isEdit
          ? t('config_areas_modal_title_edit')
          : t('config_areas_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit
          ? t('config_areas_modal_primary_edit')
          : t('config_areas_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('config_areas_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='72vw'
    >
      <div className='area-courts__layout'>
        <div className='area-courts__form'>
          <div className='row g-3'>
            <div className='col-12'>
              <Input
                label={t('config_areas_modal_name_label')}
                required
                placeholder={t('config_areas_modal_name_placeholder')}
                value={local.name}
                onChange={setField('name')}
                error={
                  local.name && !nameValid
                    ? t('config_areas_modal_name_required')
                    : ''
                }
              />
            </div>

            <div className='col-12 col-md-6'>
              <label className='form-label'>
                {t('config_areas_modal_status_label')}
              </label>
              {Dropdown ? (
                <Dropdown
                  options={statusOptions}
                  value={statusOptionFrom(local.status)}
                  onChange={(opt) =>
                    setLocal((state) => ({
                      ...state,
                      status: opt?.value || STATUS_VALUES.active,
                    }))
                  }
                  placeholder={t('config_areas_modal_status_placeholder')}
                  width='100%'
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                />
              ) : (
                <select
                  className='form-select'
                  value={local.status}
                  onChange={setField('status')}
                >
                  <option value={STATUS_VALUES.active}>
                    {t('config_areas_status_active')}
                  </option>
                  <option value={STATUS_VALUES.inactive}>
                    {t('config_areas_status_inactive')}
                  </option>
                </select>
              )}
            </div>
          </div>
        </div>

        {Table && ComboBox && (
          <div className='area-courts__section'>
            <h5 className='fw-semibold mb-2'>
              {t('config_areas_modal_assigned_courts_title')}
            </h5>
            {!isEdit || !local.id ? (
              <p className='caption font-light'>
                {t('config_areas_modal_save_to_assign')}
              </p>
            ) : (
              <>
                <div className='d-flex gap-2'>
                  <div className='w-100'>
                    <Input
                      icon='search'
                      name='searchTribunalArea'
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t('config_areas_modal_search_court')}
                      type='text'
                      value={search}
                    />
                  </div>
                  <div className='w-100'>
                    <ComboBox
                      options={regionOptions}
                      value={filterOptionFrom(regionOptions, regionFilter?.value)}
                      onChange={(opt) => setRegionFilter(opt)}
                      placeholder={t('config_areas_modal_regions_all')}
                      disabled={regionsLoading || regionOptions.length <= 1}
                    />
                  </div>
                </div>

                <nav className='area-courts__tabs' role='tablist'>
                  {tabOptions.map((option) => (
                    <button
                      key={option.value}
                      type='button'
                      className={
                        option.value === tab
                          ? 'area-courts__tab is-active'
                          : 'area-courts__tab'
                      }
                      onClick={() => setTab(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </nav>

                <div className='table-container area-courts__table'>
                  <Table
                    columns={[
                      {
                        label: (
                          <input
                            ref={headerCheckboxRef}
                            type='checkbox'
                            aria-label={t('config_areas_modal_aria_select_all')}
                            checked={allFilteredSelected}
                            onChange={toggleSelectAllFiltered}
                            disabled={filteredIds.length === 0}
                          />
                        ),
                        width: '48px',
                        render: (row) => (
                          <input
                            type='checkbox'
                            aria-label={t('config_areas_modal_aria_select_one', {
                              name: row.name,
                            })}
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleTribunal(row.id)}
                          />
                        ),
                      },
                      {
                        accessor: 'name',
                        label: t('config_areas_modal_col_court'),
                      },
                      {
                        accessor: 'region',
                        label: t('config_areas_modal_col_region'),
                      },
                    ]}
                    data={filteredTribunals}
                    loading={tribunalsLoading}
                  />
                  <div className='roleo-modal__footer'>
                    <span className='caption font-light'>
                      {t('config_areas_modal_selected_count', {
                        count: selectedIds.length,
                      })}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
